import { useState, useEffect } from 'react';
import {
	Alert,
	Box,
	Button,
	Grid,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Paper,
	Stack,
	Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import Modal from './Modal';
import { PLAN_COMPARISON_ROWS, PRICING_TIERS } from '../../Pages/Landing/constants';
import {
	deactivateTrialAccount,
	startConversionCheckout,
	clearLocalAuth,
} from '../../Shared/Api';

const FeatureIcon = ({ included, lostOnBasic }) => {
	if (included) {
		return <CheckIcon color="success" fontSize="small" />;
	}
	return (
		<CloseIcon
			fontSize="small"
			sx={{ color: lostOnBasic ? 'warning.main' : 'text.disabled' }}
		/>
	);
};

const PlanCard = ({ tier, selected, onSelect, emphasizeLosses }) => (
	<Paper
		elevation={selected ? 4 : 0}
		onClick={() => onSelect(tier.id)}
		sx={{
			p: 2,
			height: '100%',
			borderRadius: 2,
			border: '2px solid',
			borderColor: selected ? 'warning.main' : 'divider',
			cursor: 'pointer',
			display: 'flex',
			flexDirection: 'column',
		}}
	>
		<Stack direction="row" alignItems="baseline" spacing={0.75} sx={{ mb: 0.5 }}>
			<Typography variant="h6" sx={{ fontWeight: 700 }}>
				{tier.name}
			</Typography>
			<Typography variant="h5" sx={{ fontWeight: 800 }}>
				${tier.price}
			</Typography>
			<Typography variant="body2" color="text.secondary">
				/mo
			</Typography>
		</Stack>
		<Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
			{tier.tagline}
		</Typography>
		<List dense disablePadding>
			{PLAN_COMPARISON_ROWS.map((row) => {
				const included = tier.id === 'pro' ? row.pro : row.basic;
				const lost = emphasizeLosses && tier.id === 'basic' && row.lostOnBasic;
				return (
					<ListItem key={row.label} disableGutters sx={{ py: 0.25 }}>
						<ListItemIcon sx={{ minWidth: 28 }}>
							<FeatureIcon included={included} lostOnBasic={lost} />
						</ListItemIcon>
						<ListItemText
							primary={
								lost ? `${row.label} (included in your trial)` : row.label
							}
							slotProps={{
								primary: {
									variant: 'body2',
									color: lost ? 'warning.main' : 'text.primary',
									fontWeight: lost ? 600 : 400,
								},
							}}
						/>
					</ListItem>
				);
			})}
		</List>
	</Paper>
);

const ChoosePlanModal = ({
	open,
	required = false,
	onClose,
}) => {
	const [selectedPlan, setSelectedPlan] = useState('pro');
	const [submitting, setSubmitting] = useState(false);
	const [confirmDeactivate, setConfirmDeactivate] = useState(false);
	const [error, setError] = useState('');

	useEffect(() => {
		if (!open) {
			return undefined;
		}
		setConfirmDeactivate(false);
		setError('');
		setSubmitting(false);
		return undefined;
	}, [open]);

	const handleCheckout = async () => {
		setError('');
		setSubmitting(true);
		try {
			const response = await startConversionCheckout(selectedPlan);
			if (!response?.checkout_url) {
				throw new Error('Checkout could not be started.');
			}
			window.location.assign(response.checkout_url);
		} catch (err) {
			setError(
				err?.data?.error ||
					err?.message ||
					'Could not start checkout. Please try again.',
			);
			setSubmitting(false);
		}
	};

	const handleDeactivate = async () => {
		setError('');
		setSubmitting(true);
		try {
			await deactivateTrialAccount();
			clearLocalAuth();
			window.location.assign('/login?deactivated=1');
		} catch (err) {
			setError(
				err?.data?.error ||
					err?.message ||
					'Could not deactivate this account.',
			);
			setSubmitting(false);
		}
	};

	return (
		<Modal
			open={open}
			onClose={required ? () => {} : onClose}
			modalTitle={required ? 'Your free trial has ended' : 'Choose a plan'}
			maxWidth="md"
			alwaysMaxWidth
			disableBackdropClick={required}
			disableEscapeKeyDown={required}
			hideCloseButton={required}
		>
			<Stack spacing={2}>
				<Typography variant="body1" color="text.secondary">
					{required
						? 'Pick Basic or Pro to keep using Bidzbase, or deactivate the account. Your trial included a few Pro tools — those stay only on Pro.'
						: 'Finish signup anytime. You will be billed on the 1st of the month, prorated if you start mid-month.'}
				</Typography>
				{error && <Alert severity="error">{error}</Alert>}
				<Grid container spacing={2}>
					{PRICING_TIERS.map((tier) => (
						<Grid key={tier.id} size={{ xs: 12, md: 6 }}>
							<PlanCard
								tier={tier}
								selected={selectedPlan === tier.id}
								onSelect={setSelectedPlan}
								emphasizeLosses
							/>
						</Grid>
					))}
				</Grid>
				<Button
					variant="contained"
					color="warning"
					size="large"
					disabled={submitting}
					onClick={handleCheckout}
				>
					{submitting
						? 'Redirecting to checkout...'
						: `Continue with ${selectedPlan === 'pro' ? 'Pro' : 'Basic'}`}
				</Button>
				{required && (
					<Box>
						{!confirmDeactivate ? (
							<Button
								color="inherit"
								disabled={submitting}
								onClick={() => setConfirmDeactivate(true)}
							>
								Deactivate this account instead
							</Button>
						) : (
							<Alert
								severity="warning"
								action={
									<Button
										color="inherit"
										size="small"
										disabled={submitting}
										onClick={handleDeactivate}
									>
										Deactivate
									</Button>
								}
							>
								This signs everyone out and keeps the email from starting
								another free trial. Customer bid links still work.
							</Alert>
						)}
					</Box>
				)}
			</Stack>
		</Modal>
	);
};

export default ChoosePlanModal;
